import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ClientService } from './client.service';
import { catchError, tap, filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ClientDataCacheService {
  private clientDataCache$ = new BehaviorSubject<any>(null);
  private currentSubdomain: string | null = null;
  private isLoading = false;

  constructor(private clientService: ClientService) {}

  /**
   * Get cached client data or load from API if not cached
   * Prevents duplicate API calls across components
   */
  getClientData(subdomain: string): Observable<any> {
    const cachedData = this.clientDataCache$.value;

    // Return cached data if already loaded for this subdomain
    if (cachedData && this.currentSubdomain === subdomain) {
      return of(cachedData);
    }

    // Return observable if already loading to prevent duplicate requests
    if (this.isLoading && this.currentSubdomain === subdomain) {
      return this.clientDataCache$
        .asObservable()
        .pipe(filter((data) => data !== null));
    }

    this.isLoading = true;
    this.currentSubdomain = subdomain;

    return this.clientService.getClientData(subdomain).pipe(
      tap((data) => {
        this.clientDataCache$.next(data);
        this.isLoading = false;
      }),
      catchError(() => {
        this.isLoading = false;
        return of(null);
      }),
    );
  }

  /**
   * Get the cached data synchronously (after initialization)
   */
  getCachedData(): any {
    return this.clientDataCache$.value;
  }

  /**
   * Get cached data as observable for components
   */
  getCachedData$(): Observable<any> {
    return this.clientDataCache$.asObservable();
  }

  /**
   * Clear cache (useful for subdomain changes)
   */
  clearCache(): void {
    this.clientDataCache$.next(null);
    this.currentSubdomain = null;
    this.isLoading = false;
  }
}
